import { api } from "./api";

/** Which records a role's holders see. A person's widest scope across their roles wins. */
export type RecordScope = "own" | "territory" | "all";

export interface OrgRole {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  is_app_admin: boolean;
  capabilities: string[];
  admin_capabilities: string[];
  record_scope: RecordScope;
}

export interface OrgUser {
  user_id: string;
  membership_id: string;
  email: string;
  display_name: string;
  status: string;
  membership_status: string;
  is_org_admin: boolean;
  is_app_admin: boolean;
  crm_role: "app_user" | "app_admin" | null;
  crm_seat: boolean;
  timesheet_seat?: boolean;
  expense_seat?: boolean;
  capabilities: string[];
  roles?: Array<{ id: string; code: string; name: string; is_app_admin: boolean }>;
  role_ids?: string[];
  department?: string | null;
  reports_to_user_id?: string | null;
  reports_to_email?: string | null;
  reports_to_name?: string | null;
  territory_id?: string | null;
  territory_code?: string | null;
  territory_name?: string | null;
  setup_token?: string;
}

export interface TabPermission {
  capability: string;
  label: string;
  app_code: string;
  min_tier?: string;
}

export interface OpenWorkItem {
  key: string;
  label: string;
  count: number;
}

export interface SalesManagerOption {
  user_id: string;
  display_name: string;
  email: string;
}

export interface OpenWorkPreview {
  total: number;
  items: OpenWorkItem[];
  sales_managers: SalesManagerOption[];
}

export interface UsersResponse {
  users: OrgUser[];
  licensed_capabilities: string[];
  tabs: TabPermission[];
  admin_only_tabs?: TabPermission[];
  roles?: OrgRole[];
  can_create_custom_roles?: boolean;
}

export interface ImportRow {
  row_number: number;
  record_type: string;
  external_key: string;
  status: string;
  errors: string[];
  payload: Record<string, string>;
}

export interface ImportPreview {
  import_id: string;
  kind: string;
  status?: string;
  row_count: number;
  valid_count: number;
  invalid_count: number;
  can_commit: boolean;
  mapping?: Record<string, Record<string, string>>;
  detected_headers?: Record<string, string[]>;
  canonical_fields?: Record<string, string[]>;
  issues?: Array<{ record_type: string; row_number?: string; message: string }>;
  rows: ImportRow[];
}

export interface AuditEvent {
  id: string;
  created_at: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  resource_label?: string | null;
  resource_code?: string | null;
  record_guid?: string | null;
  old_value: unknown;
  new_value: unknown;
  ip: string | null;
  user_agent: string | null;
  source: "regional" | "platform";
}

export interface OrgAppSettings {
  timezone: string;
  currency: string;
  currency_symbol: string;
  quote_approval_required: boolean;
  display_name?: string;
  company_address_line1?: string;
  company_address_line2?: string;
  company_address_line3?: string;
  company_address_line4?: string;
  tax_number?: string;
  tax_rate?: number | string | null;
  has_logo?: boolean;
  fiscal_year_start_month?: number;
  history_after_days?: number;
  /** ISO 3166-1 alpha-2. Lead Generator searches postcodes in this country. */
  country?: string;
  /** The organization's own website. Needed for LeadGen (AI rewrite reads it). */
  website?: string;
  /** Display only. Does not change stored dates. */
  date_format?: string;
  /** Display only. 12 or 24. */
  time_format?: string;
  /** Display only. Does not change stored amounts. */
  number_format?: string;
  /** When on, a daily job stores European Central Bank rates for this organization. */
  forex_pull_enabled?: boolean;
  /** Https address for latest rates. Blank on the server means the built-in Frankfurter address. */
  forex_url?: string;
  /** Choices on Log activity, and which sales-plan KPI each one counts toward. */
  activity_types?: Array<{ value: string; label: string; measurement: string | null; meaning?: string }>;
  currencies?: Array<{ code: string; symbol: string }>;
}

export interface AuditLogResponse {
  events: AuditEvent[];
  has_more: boolean;
}

function orgBase(orgId: string): string {
  return `/org/${encodeURIComponent(orgId)}`;
}

export const orgSettingsApi = {
  listUsers: (orgId: string) => api.get<UsersResponse>(`${orgBase(orgId)}/users`),
  createUser: (orgId: string, body: {
    email: string;
    display_name: string;
    department?: string;
    crm_role?: "app_user" | "app_admin";
    is_org_admin: boolean;
    is_app_admin: boolean;
    assign_crm_seat?: boolean;
    capabilities: string[];
    role_ids?: string[];
    reports_to_user_id?: string | null;
    territory_id?: string | null;
  }) => api.post<OrgUser>(`${orgBase(orgId)}/users`, body),
  updateUser: (orgId: string, userId: string, body: Record<string, unknown>) =>
    api.patch<OrgUser>(`${orgBase(orgId)}/users/${encodeURIComponent(userId)}`, body),
  openItems: (orgId: string, userId: string) =>
    api.get<OpenWorkPreview>(`${orgBase(orgId)}/users/${encodeURIComponent(userId)}/open-items`),
  listRoles: (orgId: string) =>
    api.get<{
      roles: OrgRole[];
      tabs: TabPermission[];
      admin_only_tabs?: TabPermission[];
      licensed_capabilities: string[];
      can_create_custom_roles?: boolean;
    }>(`${orgBase(orgId)}/roles`),
  createRole: (orgId: string, body: {
    name: string; description?: string; capabilities?: string[]; record_scope?: RecordScope;
  }) =>
    api.post<OrgRole>(`${orgBase(orgId)}/roles`, body),
  updateRole: (orgId: string, roleId: string, body: {
    capabilities: string[]; admin_capabilities: string[]; record_scope?: RecordScope;
  }) =>
    api.patch<OrgRole>(`${orgBase(orgId)}/roles/${encodeURIComponent(roleId)}`, body),
  deleteRole: (orgId: string, roleId: string) =>
    api.delete<{ deleted: boolean }>(`${orgBase(orgId)}/roles/${encodeURIComponent(roleId)}`),
  issueSetupLink: (orgId: string, userId: string, deliver: "email" | "copy") =>
    api.post<{ setup_token: string; email: string; emailed: boolean; delivery: "sent" | "copied" | "not_configured" | "failed" }>(
      `${orgBase(orgId)}/users/${encodeURIComponent(userId)}/setup-link`,
      { deliver },
    ),
  downloadUserTemplate: (orgId: string) =>
    api.download(`${orgBase(orgId)}/imports/templates/users`, "users-import-template.csv"),
  downloadCrmTemplate: (orgId: string) =>
    api.download(`${orgBase(orgId)}/imports/templates/crm`, "crm-import-template.zip"),
  previewUsers: (orgId: string, file: File) =>
    api.upload<ImportPreview>(`${orgBase(orgId)}/imports/users/preview`, file),
  commitUsers: (orgId: string, importId: string) =>
    api.post<ImportPreview>(`${orgBase(orgId)}/imports/users/commit`, { import_id: importId }),
  previewCrm: (orgId: string, file: File) =>
    api.upload<ImportPreview>(`${orgBase(orgId)}/imports/crm/preview`, file),
  mapCrm: (orgId: string, importId: string, mapping: Record<string, Record<string, string>>) =>
    api.post<ImportPreview>(`${orgBase(orgId)}/imports/crm/mapping`, { import_id: importId, mapping }),
  commitCrm: (orgId: string, importId: string) =>
    api.post<ImportPreview>(`${orgBase(orgId)}/imports/crm/commit`, { import_id: importId }),
  downloadRolesTemplate: (orgId: string) =>
    api.download(`${orgBase(orgId)}/imports/templates/roles`, "roles-import-template.csv"),
  previewRoles: (orgId: string, file: File) =>
    api.upload<ImportPreview>(`${orgBase(orgId)}/imports/roles/preview`, file),
  commitRoles: (orgId: string, importId: string) =>
    api.post<ImportPreview>(`${orgBase(orgId)}/imports/roles/commit`, { import_id: importId }),
  listAuditLog: (orgId: string, opts: { q?: string; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.q?.trim()) params.set("q", opts.q.trim());
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.offset) params.set("offset", String(opts.offset));
    const query = params.toString();
    return api.get<AuditLogResponse>(`${orgBase(orgId)}/audit-log${query ? `?${query}` : ""}`);
  },
  setupPassword: (token: string, newPassword: string) =>
    api.post<{ status: string }>("/auth/password-setup", { token, new_password: newPassword }),
  resendSetupLink: (email: string) =>
    api.post<{ status: string }>("/auth/password-setup/resend", { email }),
  requestPasswordReset: (email: string) =>
    api.post<{ status: string }>("/auth/password-reset", { email }),
  getAppSettings: (orgId: string) => api.get<OrgAppSettings>(`${orgBase(orgId)}/settings`),
  getNetwork: (orgId: string) =>
    api.get<{
      cidrs: string[];
      available: boolean;
      enforced: boolean;
      tier: string | null;
      max_cidrs: number;
    }>(`${orgBase(orgId)}/settings/network`),
  saveNetwork: (orgId: string, cidrs: string[]) =>
    api.put<{
      cidrs: string[];
      available: boolean;
      enforced: boolean;
      tier: string | null;
      max_cidrs: number;
    }>(`${orgBase(orgId)}/settings/network`, { cidrs }),
  saveAppSettings: (orgId: string, body: {
    timezone: string;
    currency: string;
    currency_symbol?: string | null;
    quote_approval_required: boolean;
    company_address_line1?: string | null;
    company_address_line2?: string | null;
    company_address_line3?: string | null;
    company_address_line4?: string | null;
    tax_number?: string | null;
    tax_rate?: number | string | null;
    fiscal_year_start_month?: number;
    history_after_days?: number;
    date_format?: string;
    time_format?: string;
    number_format?: string;
    activity_types?: Array<{ value: string; label: string; measurement: string | null }> | null;
  }) => api.put<OrgAppSettings>(`${orgBase(orgId)}/settings`, body),
  listActivityTypes: (orgId: string) =>
    api.get<{ activity_types: NonNullable<OrgAppSettings["activity_types"]> }>(
      `${orgBase(orgId)}/settings/activity-types`,
    ).then((body) => body.activity_types),
  getCompanyLogo: (orgId: string) => api.getBlob(`${orgBase(orgId)}/settings/logo`),
  uploadCompanyLogo: (orgId: string, file: File) =>
    api.upload<{ content_type: string; bytes: number }>(`${orgBase(orgId)}/settings/logo`, file),
  deleteCompanyLogo: (orgId: string) => api.delete<{ ok: boolean }>(`${orgBase(orgId)}/settings/logo`),
  setAppStatus: (orgId: string, appCode: "timesheet" | "expense", enabled: boolean) =>
    api.put<{ app_code: string; enabled: boolean }>(
      `${orgBase(orgId)}/access/apps/${appCode}/status`,
      { enabled },
    ),
};
